/* Energy Unleashed — Loox carousel type size.
 *
 * The review carousel under the three-step section renders its review copy at
 * 24px and clamps it to 6 lines, so a full-length review is cut off mid
 * sentence. Smaller type and a higher line cap let the whole review show.
 *
 * WHY THIS IS JAVASCRIPT AND NOT CSS.
 *
 * `<loox-dynamic-carousel-widget>` is a Lit element that renders into an OPEN
 * shadow root, and its stylesheet declares the three custom properties
 *
 *     --loox-u-dynamic-carousel-review-font-size
 *     --loox-u-dynamic-carousel-review-line-height
 *     --loox-u-dynamic-carousel-review-max-lines
 *
 * directly on `.review-text` itself. Custom properties do inherit through a
 * shadow boundary, so setting them on the host looks like it should work — it
 * cannot, because a declaration ON an element always beats a value inherited
 * into it, `!important` on the host included. The only way in is a stylesheet
 * inside the same shadow root, which an open root permits.
 *
 * If Loox ever exposes a font-size setting on the app block, delete this and
 * use that instead.
 */
(function () {
  'use strict';

  /* Desktop and phone are set separately on purpose. A phone is held closer but
   * has far less width, so the size that reads as calm on a monitor reads as
   * cramped in the hand — 14px on mobile against 13px on desktop. */
  /* Landed by eye, not by formula: 17 and 15 both read too large, 13 too small.
   * Mobile happens to want the same number for the opposite reason — less width
   * but a closer viewing distance. Kept as two constants anyway, because the
   * next nudge will almost certainly move only one of them. */
  var FONT_SIZE = '17px';         /* 24 -> 17 -> 15 -> 13 -> 14 -> back to 17 */
  var FONT_SIZE_MOBILE = '14px';
  var MAX_LINES = '12';     /* from 6 — the block truncates at 200 characters
                               anyway, so this caps nothing a reader wants */
  var LINE_HEIGHT = '1.45';

  /* Loox styles mobile separately, at max-width:720px, with its OWN size (20px)
   * and a 3-line clamp. The !important below already outranks it, but stating
   * mobile explicitly means the phone size is a decision here rather than a
   * side effect of one rule leaking across a breakpoint — and it survives Loox
   * changing their mobile rule. */
  function rule(sel, size) {
    return sel + '{' +
      '--loox-u-dynamic-carousel-review-font-size:' + size + ' !important;' +
      '--loox-u-dynamic-carousel-review-max-lines:' + MAX_LINES + ' !important;' +
      '--loox-u-dynamic-carousel-review-line-height:' + LINE_HEIGHT + ' !important;' +
      'font-size:' + size + ' !important;' +
    '}';
  }

  var SEL = '.loox-dynamic-carousel-widget .review-text';
  var CSS = rule(SEL, FONT_SIZE) +
            '@media screen and (max-width:720px){' + rule(SEL, FONT_SIZE_MOBILE) + '}';

  function inject(root) {
    if (!root || root.querySelector('style[data-eu-loox]')) return true;
    var s = document.createElement('style');
    s.setAttribute('data-eu-loox', '');
    s.textContent = CSS;
    root.appendChild(s);
    return true;
  }

  /* Returns true only once every carousel on the page has been styled, so the
   * observer below knows when it can stop watching. */
  function scan() {
    var hosts = document.querySelectorAll('loox-dynamic-carousel-widget');
    if (!hosts.length) return false;
    var all = true;
    for (var i = 0; i < hosts.length; i++) {
      if (hosts[i].shadowRoot) inject(hosts[i].shadowRoot);
      else all = false;
    }
    return all;
  }

  function start() {
    /* Poll, and keep polling.
     *
     * The Loox loader injects the carousel script only when the widget nears
     * the viewport, so on a long page the shadow root may not exist for a
     * minute or more. An earlier version gave up after 15 seconds, which was
     * fine on desktop — where the carousel is reached quickly — and silently
     * did nothing on mobile, where the page is longer and slower. That is
     * exactly the shape of "it works on desktop but not on my phone".
     *
     * Checking a .shadowRoot property on one or two known elements is close to
     * free, so this runs at a slow tick for as long as a session realistically
     * lasts rather than racing a timeout. It also does not stop after the first
     * success: if Lit re-renders and drops our <style>, the next tick puts it
     * back. */
    var ticks = 0;
    var MAX = 2400;              /* 500ms x 2400 = 20 minutes */
    scan();
    var timer = setInterval(function () {
      scan();
      if (++ticks > MAX) clearInterval(timer);
    }, 500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
